import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ThemeIcon } from '../../icons/ThemeIcon.js';
import { PlaceholderMedia } from '../../primitives/PlaceholderMedia.js';
import { productImageAlt } from '../../blocks/_shared/imageAlt.js';
import { renderCountdownBadge } from './CountdownBadge.js';
import { UrgencyBadge } from './UrgencyBadge.js';
const CARD_RATIO = {
    square: '1 / 1',
    portrait: '4 / 5',
    landscape: '3 / 2',
};
/** Five stars at a fixed demo rating (real per-product ratings arrive with the reviews feature wiring). */
function RatingStars({ value = 4 }) {
    return (_jsx("span", { "aria-hidden": "true", style: { display: 'inline-flex', gap: '1px', color: 'var(--aqx-theme-star)' }, children: [0, 1, 2, 3, 4].map((i) => (_jsx(ThemeIcon, { id: i < value ? 'star' : 'star-empty', size: 14 }, i))) }));
}
export function ProductCard({ product, context, options }) {
    const o = options ?? {};
    const ratio = CARD_RATIO[o.imageRatio ?? 'portrait'];
    const cover = product.images[0];
    const href = context.resolveUrl({ type: 'product', slug: product.slug });
    const price = product.price;
    const compareAt = product.compareAtPrice;
    const showCompare = o.showCompareAt !== false && compareAt != null && compareAt.amount > price.amount;
    const savings = showCompare && compareAt ? compareAt.amount - price.amount : 0;
    return (_jsxs("div", { style: cardStyle, children: [_jsxs("a", { href: href, style: { textDecoration: 'none', color: 'inherit', display: 'block' }, children: [_jsxs("div", { style: { ...frameStyle, aspectRatio: ratio, position: 'relative' }, children: [renderCountdownBadge(context.countdownBadges, product.id), _jsx(UrgencyBadge, { product: product, context: context }), cover?.url ? (_jsx("img", { src: cover.url, alt: productImageAlt(cover.alt, product.name), loading: "lazy", style: {
                                    inlineSize: '100%',
                                    blockSize: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    // Chrome paints the alt text in the box while the photo loads — keep the words invisible.
                                    color: 'transparent',
                                } })) : (_jsx(PlaceholderMedia, {}))] }), o.showVendor && o.vendor ? _jsx("p", { style: vendorStyle, children: o.vendor }) : null, _jsx("h3", { style: titleStyle, children: product.name }), o.showRating ? (_jsx("div", { style: { marginTop: 'var(--aqx-theme-space-1, 4px)' }, children: _jsx(RatingStars, {}) })) : null, o.showPrice !== false ? (_jsxs("div", { style: priceRowStyle, children: [_jsx("span", { style: { fontWeight: 'var(--aqx-theme-fw-semibold, 600)' }, children: context.formatMoney(price) }), showCompare && compareAt ? (_jsx("span", { style: struckStyle, children: context.formatMoney(compareAt) })) : null, savings > 0 ? (_jsxs("span", { style: savingsStyle, children: ["\u2212", context.formatMoney({ amount: savings, currency: price.currency })] })) : null] })) : null] }), o.quickAdd ? (_jsx("button", { type: "button", style: quickAddStyle, children: o.quickAddLabel ?? 'Ajouter' })) : null] }));
}
// ── styles ───────────────────────────────────────────────────────────────────
const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--aqx-theme-space-2, 8px)',
};
const frameStyle = {
    overflow: 'hidden',
    borderRadius: 'var(--aqx-theme-radius-media, 0px)',
    background: 'var(--aqx-scheme-surface)',
    border: '1px solid var(--aqx-scheme-border)',
};
const vendorStyle = {
    margin: 'var(--aqx-theme-space-3, 12px) 0 0',
    fontSize: 'var(--aqx-theme-text-xs, 12px)',
    letterSpacing: 'var(--aqx-theme-tracking-wide, 0.08em)',
    textTransform: 'uppercase',
    color: 'var(--aqx-scheme-muted)',
};
const titleStyle = {
    margin: 'var(--aqx-theme-space-2, 8px) 0 0',
    fontSize: 'var(--aqx-theme-text-base, 16px)',
    fontWeight: 'var(--aqx-theme-fw-semibold, 600)',
    lineHeight: 'var(--aqx-theme-lh-snug, 1.25)',
};
const priceRowStyle = {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 'var(--aqx-theme-space-2, 8px)',
    marginTop: 'var(--aqx-theme-space-1, 4px)',
    fontSize: 'var(--aqx-theme-text-base, 16px)',
};
const struckStyle = {
    textDecoration: 'line-through',
    color: 'var(--aqx-scheme-muted)',
    fontSize: 'var(--aqx-theme-text-sm, 14px)',
};
const savingsStyle = {
    fontSize: 'var(--aqx-theme-text-xs, 12px)',
    fontWeight: 'var(--aqx-theme-fw-semibold, 600)',
    color: 'var(--aqx-scheme-accent)',
};
const quickAddStyle = {
    marginTop: 'var(--aqx-theme-space-1, 4px)',
    alignSelf: 'start',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'var(--aqx-theme-text-sm, 14px)',
    fontWeight: 'var(--aqx-theme-fw-semibold, 600)',
    color: 'var(--aqx-scheme-accent)',
    background: 'transparent',
    border: '1px solid var(--aqx-scheme-accent)',
    paddingBlock: 'var(--aqx-theme-space-2, 8px)',
    paddingInline: 'var(--aqx-theme-space-4, 16px)',
    borderRadius: 'var(--aqx-theme-radius-pill, 999px)',
};
//# sourceMappingURL=ProductCard.js.map